/**
 * Google Analytics Event Tracking Utility
 * Tracks Add to Cart and WhatsApp button interactions
 */

class GATracking {
  constructor() {
    this.isGA4Available = typeof gtag !== 'undefined';
    this.isGTMAvailable = typeof dataLayer !== 'undefined';
  }

  /**
   * Track Add to Cart events
   * @param {Object} productData - Product information
   * @param {string} productData.productId - Product ID
   * @param {string} productData.productTitle - Product title
   * @param {string} productData.variantId - Variant ID
   * @param {string} productData.variantTitle - Variant title
   * @param {number} productData.price - Product price
   * @param {number} productData.quantity - Quantity added
   * @param {string} productData.currency - Currency code
   * @param {string} source - Source of the event (e.g., 'product-page', 'quick-add', 'sticky-modal')
   */
  trackAddToCart(productData, source = 'unknown') {
    const eventData = {
      event_category: 'ecommerce',
      event_label: productData.productTitle,
      custom_parameters: {
        product_id: productData.productId,
        variant_id: productData.variantId,
        variant_title: productData.variantTitle,
        price: productData.price,
        quantity: productData.quantity,
        currency: productData.currency || 'USD',
        source: source
      }
    };

    // GA4 Event
    if (this.isGA4Available) {
      gtag('event', 'add_to_cart', {
        currency: productData.currency || 'USD',
        value: productData.price,
        items: [{
          item_id: productData.productId,
          item_name: productData.productTitle,
          item_variant: productData.variantId,
          price: productData.price,
          quantity: productData.quantity
        }],
        source: source
      });
    }

    // GTM Event
    if (this.isGTMAvailable) {
      dataLayer.push({
        event: 'add_to_cart',
        ecommerce: {
          currency: productData.currency || 'USD',
          value: productData.price,
          items: [{
            item_id: productData.productId,
            item_name: productData.productTitle,
            item_variant: productData.variantId,
            price: productData.price,
            quantity: productData.quantity
          }]
        },
        source: source
      });
    }

    console.log('GA Tracking - Add to Cart:', eventData);
  }

  /**
   * Track WhatsApp button clicks
   * @param {Object} productData - Product information
   * @param {string} productData.productId - Product ID
   * @param {string} productData.productTitle - Product title
   * @param {string} productData.variantId - Variant ID
   * @param {string} productData.variantTitle - Variant title
   * @param {string} source - Source of the event (e.g., 'product-page', 'sticky-modal')
   */
  trackWhatsAppClick(productData, source = 'unknown') {
    const eventData = {
      event_category: 'engagement',
      event_label: productData.productTitle,
      custom_parameters: {
        product_id: productData.productId,
        variant_id: productData.variantId,
        variant_title: productData.variantTitle,
        source: source,
        action: 'whatsapp_inquiry'
      }
    };

    // GA4 Event
    if (this.isGA4Available) {
      gtag('event', 'whatsapp_click', {
        event_category: 'engagement',
        event_label: productData.productTitle,
        custom_parameters: {
          product_id: productData.productId,
          variant_id: productData.variantId,
          variant_title: productData.variantTitle,
          source: source
        }
      });
    }

    // GTM Event
    if (this.isGTMAvailable) {
      dataLayer.push({
        event: 'whatsapp_click',
        event_category: 'engagement',
        event_label: productData.productTitle,
        product_id: productData.productId,
        variant_id: productData.variantId,
        variant_title: productData.variantTitle,
        source: source
      });
    }

    console.log('GA Tracking - WhatsApp Click:', eventData);
  }

  /**
   * Extract product data from form or context
   * @param {HTMLElement} context - Form or container element
   * @returns {Object} Product data object
   */
  extractProductData(context) {
    const form = context.closest('form') || context;
    const variantInput = form.querySelector('input[name="id"]');
    const quantityInput = form.querySelector('input[name="quantity"]');
    
    // Get product data from page
    const productJson = document.querySelector('#ProductJson');
    const product = productJson ? JSON.parse(productJson.textContent) : null;
    
    // Get current variant
    const currentVariantId = variantInput ? variantInput.value : null;
    const currentVariant = product && currentVariantId ? 
      product.variants.find(v => v.id.toString() === currentVariantId) : null;

    return {
      productId: product ? product.id.toString() : null,
      productTitle: product ? product.title : document.title,
      variantId: currentVariantId,
      variantTitle: currentVariant ? currentVariant.title : null,
      price: currentVariant ? currentVariant.price / 100 : 0, // Convert from cents
      quantity: quantityInput ? parseInt(quantityInput.value) || 1 : 1,
      currency: product ? product.price_currency : 'USD'
    };
  }
}

// Initialize GA Tracking
window.GATracking = new GATracking();

// Export for module systems
if (typeof module !== 'undefined' && module.exports) {
  module.exports = GATracking;
}

